/**
 * PlanView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { createPlanner, adjustSegmentsForDescent, buildCCRConfig, planDive, previousDiveRecord, resolveSegmentGases, describeO2Flush } from '../core/DayPlanner.js'
import { Gas } from '../core/Gas.js'
import { DivePlanner } from '../core/DivePlanner.js'
import { Store } from '../store/Store.js'
import { PlanError } from '../core/errors.js'
import { PdfExport } from '../utils/PdfExport.js'
import { SavedPlans } from '../utils/SavedPlans.js'
import { i18n } from '../i18n/index.js'
import { analyzeAlerts } from '../utils/alerts.js'
import { shortGasName, gasLabels, gasConsumptionRows, filterDisplaySegments, surfaceIntervalSentences, noFlyValue, exposureShown, formatDensity, segmentDensity, densityLevel, flushFigures, flushDepthForTarget, planEnvironment } from '../utils/planUtils.js'
import { toggleFullscreen, showToast, readBoundedNumber, escapeHtml } from '../utils/viewUtils.js'

export class PlanView {
  constructor(container) {
    this.container = container
    // No planner instance is kept: the plan is computed by DayPlanner.planDive,
    // which builds and owns its own. Holding a second, freshly built one would
    // hand virgin tissues to whoever read it back, not the state of the plan on
    // screen.
    this.results = null
    this.unsubscribe = null
    // Track the currently open modal id (if any) so a re-render triggered
    // by a store change does not silently close it
    this.openModalId = null
    // Last calculation failure, { code, message } or null. Kept on the
    // instance because clearing the results notifies the store, which
    // re-renders the whole page: the message has to survive that re-render.
    this.planError = null
    // Open state of the environment panel. Editing the water density writes to
    // the store, which re-renders this view: without this flag the panel would
    // fold back on itself at the first keystroke committed.
    this.envOpen = false
    // Labels the Gas column prints, resolved once over the whole displayed plan
    // (see gasLabelOf). Dropped whenever the results change.
    this.gasLabelCache = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  // Translate a key and log when no dictionary carries it. i18n.t() returns the
  // key itself in that case, so without this trace a raw identifier would show
  // up in the interface with nothing anywhere to explain why.
  tChecked(key, params) {
    const text = this.t(key, params)
    if (text === key) {
      console.error('PlanView: no translation for "%s", showing the raw key', key)
    }
    return text
  }

  // Translate an alert or warning key coming from the engine.
  // i18n.t() returns the key itself when it is missing from every dictionary;
  // that silent fallback would put a raw identifier in a red alert box with no
  // trace anywhere, so the missing key is logged before being displayed.
  translateAlert(key, params) {
    const message = this.t(key, params)
    if (message === key) {
      console.error('PlanView: no translation for alert key "%s" (params=%o), showing the raw key', key, params)
    }
    return message
  }

  // Check if current GF is not matching any preset
  isCustomGF(settings) {
    const presets = settings.gfPresets || []
    const currentLow = Math.round(settings.gfLow * 100)
    const currentHigh = Math.round(settings.gfHigh * 100)
    return !presets.some(p => p.gfLow === currentLow && p.gfHigh === currentHigh)
  }

  // Water type currently stored, in one of the two forms the engine accepts:
  // a Gas.WATER_PRESETS key, or a density in g/l.
  waterType() {
    return this.environment().waterType ?? Store.DEFAULT_WATER_TYPE
  }

  // Density of the stored water, in g/l, or null when the stored value is
  // unusable (the reason is logged by Store.waterDensityOrNull).
  waterDensity() {
    return Store.waterDensityOrNull(this.waterType())
  }

  // Water label of the collapsed environment title: the preset name, or the
  // density itself when the diver typed one.
  waterLabel() {
    const preset = Store.waterPresetKey(this.waterType())
    if (preset !== Store.CUSTOM_WATER) {
      const labelKey = Store.WATER_PRESET_LABEL_KEYS[preset]
      if (!labelKey) {
        console.error('PlanView: no label key declared for the water preset "%s"', preset)
        return preset
      }
      return this.tChecked(labelKey)
    }
    const density = this.waterDensity()
    return density === null ? '--' : `${density} g/l`
  }

  // Options of the water selector: the named presets, then the custom density.
  // Each preset carries its density so the diver reads what the name stands
  // for instead of having to know that sea water weighs 1030 g/l.
  renderWaterOptions(selected) {
    const options = Object.keys(Gas.WATER_PRESETS).map(key => {
      const labelKey = Store.WATER_PRESET_LABEL_KEYS[key]
      if (!labelKey) {
        console.error('PlanView: no label key declared for the water preset "%s"', key)
      }
      const label = labelKey ? this.tChecked(labelKey) : key
      return `<option value="${key}" ${selected === key ? 'selected' : ''}>${label} (${Gas.WATER_PRESETS[key]} g/l)</option>`
    })
    options.push(`<option value="${Store.CUSTOM_WATER}" ${selected === Store.CUSTOM_WATER ? 'selected' : ''}>${this.tChecked('settings_water_custom')}</option>`)
    return options.join('')
  }

  // What the selected water actually changes, in the units a diver reads:
  // the density it stands for and the metres of water column worth one bar
  // (9.90 m/bar in the sea, 10.19 m/bar in a lake).
  formatWaterFigures(density) {
    if (density === null || density === undefined) return '--'
    return `${density} g/l - ${Gas.depthPerBar(density).toFixed(2)} m/bar`
  }

  // Write one field of the environment.
  //
  // Store.update owns the invalidation rule (Store.PLAN_INPUT_PATHS carries
  // environment.waterType and environment.altitude): it drops the plan computed
  // for the previous environment before notifying its subscribers, and this
  // view re-renders on that notification, so the old stops can never be painted
  // once under the new water type or the new altitude. The difference is not
  // cosmetic - 40 m / 30 min on air at GF 30/85 with the factory settings runs
  // 78 min in fresh water, 83 min in the sea and 85 min in the Red Sea, and
  // fresh water is the only one of the four to need six deco stops instead of
  // seven.
  updateEnvironment(field, value) {
    window.store.update(`environment.${field}`, value)
  }

  // "This plan was computed with other inputs, recalculate it" notice.
  // Read from the store and not from an instance flag so it survives the
  // re-render the change itself triggers, and so it is still shown when the
  // input was changed from the Settings or the Gases screen while this view was
  // not mounted. The sentence is chosen by Store.staleNoticeKey, the single
  // place that words this event. Returns '' when the displayed plan matches
  // every input it was computed from.
  renderStaleNotice() {
    const key = Store.staleNoticeKey(window.store.state.results)
    if (!key) return ''
    return `
        <div id="plan-stale-notice" class="alert alert-warning py-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span class="text-sm">${this.tChecked(key)}</span>
        </div>`
  }

  // Banner shown while this screen holds a dive of the day.
  //
  // Without it, a diver who came from the day screen edits a dive, calculates,
  // navigates away, and the series keeps the version it had: the work is lost
  // with nothing on screen having said where this dive came from.
  renderDayEditingNotice() {
    const index = window.store.state.day?.editingIndex
    if (index === null || index === undefined) return ''

    return `
        <div id="day-editing-notice" class="alert alert-info py-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-sm flex-1">${this.t('day_editing_dive', { number: index + 1 })}</span>
          <div class="flex gap-1">
            <button id="day-save-dive-btn" class="btn btn-primary btn-xs">${this.t('day_save_into_day')}</button>
            <button id="day-detach-dive-btn" class="btn btn-ghost btn-xs">${this.t('day_detach')}</button>
          </div>
        </div>`
  }

  // Build and inject the full plan page, then attach event listeners
  render() {
    // Drop any previous store subscription before re-rendering so we never
    // accumulate duplicate listeners across re-renders.
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }

    const { segments, settings, ccr, gases, results } = window.store.state

    // Keep instance results in sync with the store so export/save buttons
    // never operate on stale data after a gas change invalidated them.
    // An empty { ccr: null, bailout: null } is the store default and also what
    // a failed calculation leaves behind: it is not a plan, so it must not
    // unhide the results panel nor let the PDF and save buttons run on it.
    this.results = (results && (results.ccr || results.bailout)) ? results : null
    this.gasLabelCache = null

    this.container.innerHTML = `
      <div class="space-y-4 pb-20">
        ${this.renderSegmentsSection(segments, gases, settings)}
        ${this.renderResultsSection(settings, ccr)}
      </div>
      ${this.renderModals(ccr, gases, settings)}
    `

    this.bindEvents()

    // Restore the calculated results panel after a re-render so that a store
    // change triggered while a plan is displayed does not blank it out.
    this.applyResults(ccr)

    // Same for the calculation error: clearing the obsolete plan notifies the
    // store, which re-renders this page, so the message is re-injected here.
    this.applyPlanError()

    // Re-render on store changes (e.g. gas edited from GasesView while this
    // view is mounted in the desktop dashboard). Skip if a modal owned by
    // this view is open to avoid closing it under the user's feet, and skip
    // if only free-text fields were modified to avoid stealing input focus.
    this.lastSnapshot = this.computeSnapshot()
    this.unsubscribe = window.store.subscribe(() => {
      if (this.isOwnModalOpen()) return
      const snap = this.computeSnapshot()
      if (snap === this.lastSnapshot) return
      this.lastSnapshot = snap
      this.render()
    })
  }

  // Snapshot of state slices whose change must force a re-render.
  // Free-text inputs (diveSite, diverNames, diveNotes) are excluded so
  // typing into them does not destroy and recreate the input element.
  computeSnapshot() {
    const s = window.store.state
    return JSON.stringify({
      gases: s.gases,
      diluents: s.diluents,
      segments: s.segments,
      results: s.results,
      ccr: s.ccr,
      environment: s.environment,
      gf: [s.settings.gfLow, s.settings.gfHigh],
      units: s.settings.units,
      schoolMode: s.settings.schoolMode,
      gasSwitchAtStop: s.settings.gasSwitchAtStop
    })
  }

  // True when one of this view's dialogs is currently open
  isOwnModalOpen() {
    const ids = ['segment-modal', 'custom-gf-modal', 'save-plan-modal', 'pdf-format-modal', 'flush-depth-modal']
    return ids.some(id => {
      const el = document.getElementById(id)
      return el && el.open
    })
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
  }

  // Re-populate the results panels from this.results (set in calculate() and
  // synced from the store on each render). No-op when no plan is computed.
  applyResults(ccr) {
    if (!this.results) return
    const container = document.getElementById('results-container')
    if (!container) return

    if (ccr.enabled && this.results.ccr) {
      this.renderCCRResults(this.results.ccr)
      if (this.results.bailout) {
        this.renderBailoutResults(this.results.bailout, true)
        if (this.results.bailout.gasConsumption) {
          this.renderGasConsumption(this.results.bailout.gasConsumption)
        }
      }
      document.getElementById('ccr-results')?.classList.remove('hidden')
      document.getElementById('gas-consumption')?.classList.remove('hidden')
    } else if (this.results.bailout) {
      this.renderBailoutResults(this.results.bailout, false)
      if (this.results.bailout.gasConsumption) {
        this.renderGasConsumption(this.results.bailout.gasConsumption, false)
      }
      document.getElementById('ccr-results')?.classList.add('hidden')
      document.getElementById('gas-consumption')?.classList.remove('hidden')
    }
    container.classList.remove('hidden')
  }

  // Inject the current calculation error (this.planError) into #plan-error,
  // or empty the block when the last calculation succeeded.
  // Built with the DOM API and textContent: the technical detail of an
  // unexpected exception is not a translated string and must never be parsed
  // as markup.
  applyPlanError() {
    const container = document.getElementById('plan-error')
    if (!container) {
      if (this.planError) {
        console.error('PlanView: #plan-error is missing, cannot display the error %o', this.planError)
      }
      return
    }

    container.innerHTML = ''
    if (!this.planError) {
      container.classList.add('hidden')
      return
    }

    const box = document.createElement('div')
    box.className = 'alert alert-error shadow-sm'

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    icon.setAttribute('class', 'stroke-current shrink-0 h-6 w-6')
    icon.setAttribute('fill', 'none')
    icon.setAttribute('viewBox', '0 0 24 24')
    const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    iconPath.setAttribute('stroke-linecap', 'round')
    iconPath.setAttribute('stroke-linejoin', 'round')
    iconPath.setAttribute('stroke-width', '2')
    iconPath.setAttribute('d', 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z')
    icon.appendChild(iconPath)
    box.appendChild(icon)

    const body = document.createElement('div')
    const title = document.createElement('h3')
    title.className = 'font-bold'
    title.textContent = this.t('plan_error_title')
    body.appendChild(title)

    const text = document.createElement('div')
    text.className = 'text-sm'
    text.textContent = this.planError.message
    body.appendChild(text)
    box.appendChild(body)

    container.appendChild(box)
    container.classList.remove('hidden')
  }

  // Remove every trace of a previously computed plan.
  // A plan left on screen after a failed calculation is the dangerous case:
  // the diver would read stops that no longer correspond to the parameters.
  clearResultsDisplay() {
    this.results = null
    this.gasLabelCache = null

    for (const id of ['ccr-table', 'ccr-summary', 'bailout-table', 'bailout-summary', 'gas-table']) {
      const el = document.getElementById(id)
      if (el) el.innerHTML = ''
    }
    document.getElementById('ccr-results')?.classList.add('hidden')
    document.getElementById('gas-consumption')?.classList.add('hidden')
    document.getElementById('results-container')?.classList.add('hidden')
  }

  // Build the message of a failed calculation and display it.
  // `error` is a PlanError (code taken from PLAN_ERROR_CODES, already
  // translated in the six dictionaries) or any other exception.
  showPlanError(error) {
    const code = error instanceof PlanError ? error.code : null
    let message
    if (code) {
      message = this.t(code, error.details || {})
      if (message === code) {
        // The core declares a code the dictionaries do not carry yet: show the
        // technical identifier rather than an empty error box, and log it.
        console.error('PlanView: no translation for plan error code "%s" (details=%o)', code, error.details)
        message = `${code} ${JSON.stringify(error.details || {})}`
      }
    } else {
      message = error?.message ? `${error.name || 'Error'}: ${error.message}` : String(error)
    }

    this.planError = { code, message }
    this.applyPlanError()
  }

  // Segment list, GF preset bar, dive info, environment settings, and calculate button
  // Deliberate loop flushes, editable only on a rebreather dive and only when
  // the diver has switched the feature on. Nothing is rendered otherwise: the
  // plan screen belongs to open circuit divers too, and an editor for a
  // manoeuvre they never perform is one more row to read past.
  //
  // The mixes come from the diluent list, which is what a flush gas physically
  // is: a gas pushed into the loop through the ADV or the manual add valve.
  renderFlushesSection(settings) {
    const { ccr, diluents } = window.store.state
    const editorOn = settings.ccrFlushEnabled === true
    const o2FlushOn = settings.ccrO2FlushEnabled === true
    if (!ccr.enabled || (!editorOn && !o2FlushOn)) return ''

    const flushes = editorOn && Array.isArray(ccr.flushes) ? ccr.flushes : []
    const options = diluents
      .map((d, index) => ({ d, index }))
      .filter(({ d }) => d.enabled)

    // What each flush is really worth, under the row that describes it. The
    // depth alone says nothing: air at 35 m and air at 55 m are the same row
    // and two very different minutes, and during a flush the setpoint does not
    // hold, so none of the other CCR columns answers for it.
    const figuresOf = (flush) => {
      const mix = diluents[flush?.diluentIndex]
      const figures = flushFigures(mix, flush?.depth, planEnvironment())
      if (!figures) return ''
      return `<p class="text-xs opacity-70 mt-1">${this.t('plan_flush_figures', {
        gas: mix.name,
        depth: flush.depth,
        ppO2: figures.ppO2.toFixed(2),
        end: Math.round(figures.end),
        density: figures.density.toFixed(1)
      })}</p>`
    }

    return `
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('plan_flush')}</h2>
              ${editorOn ? `<button id="add-flush-btn" class="btn btn-primary btn-xs">+ ${this.t('plan_flush_add')}</button>` : ''}
            </div>
            <p class="text-xs opacity-70">${this.t('plan_flush_hint')}</p>
            ${flushes.length === 0 ? '' : `<p class="text-xs opacity-70">${this.t('plan_flush_keep_hint')}</p>`}

            ${this.renderO2FlushRow(settings)}

            ${editorOn ? `
            <div id="flushes-list" class="space-y-2 mt-2">
              ${flushes.length === 0
        ? `<p class="text-sm opacity-70">${this.t('plan_flush_none')}</p>`
        : flushes.map((flush, i) => `
              <div data-flush-row="${i}">
              <div class="flex items-end gap-2 flex-wrap">
                <!-- Mix first: the depth helper computes from it, so picking it
                     last would mean going right, then back left to compute -->
                <div class="form-control">
                  <label class="label py-0"><span class="label-text text-xs">${this.t('plan_flush_gas')}</span></label>
                  <select class="select select-bordered select-sm flush-diluent" data-flush-index="${i}">
                    ${options.map(({ d, index }) => `
                      <option value="${index}" ${index === flush.diluentIndex ? 'selected' : ''}>${escapeHtml(d.name)}</option>
                    `).join('')}
                  </select>
                </div>
                <div class="form-control">
                  <label class="label py-0"><span class="label-text text-xs">${this.t('plan_flush_depth')}</span></label>
                  <div class="join">
                    <input type="number" class="input input-bordered input-sm w-20 join-item flush-depth"
                      data-flush-index="${i}" value="${flush.depth ?? ''}" min="1" step="1" />
                    <button class="btn btn-outline btn-sm join-item flush-helper" data-flush-index="${i}">
                      ${this.t('plan_flush_helper')}
                    </button>
                  </div>
                </div>
                <div class="form-control">
                  <label class="label py-0"><span class="label-text text-xs">${this.t('plan_flush_time')}</span></label>
                  <input type="number" class="input input-bordered input-sm w-24 flush-time"
                    data-flush-index="${i}" value="${flush.time ?? ''}" min="1" step="1" />
                </div>
                <div class="form-control">
                  <label class="label cursor-pointer gap-2 py-0" title="${this.t('plan_flush_keep_hint')}">
                    <input type="checkbox" class="checkbox checkbox-sm flush-keep"
                      data-flush-index="${i}" ${flush.keepMix === true ? 'checked' : ''} />
                    <span class="label-text text-xs">${this.t('plan_flush_keep')}</span>
                  </label>
                </div>
                <button class="btn btn-ghost btn-sm text-error flush-remove" data-flush-index="${i}">
                  ${this.t('plan_flush_remove')}
                </button>
              </div>
              ${figuresOf(flush)}
              </div>
            `).join('')
      }
            </div>` : ''}
          </div>
        </div>
    `
  }

  // Finishing the dive on oxygen, read only: it is described in the settings,
  // not here. Shown all the same, because it puts a row in the computed ascent
  // and leaves the loop on oxygen above it - a line the diver must be able to
  // trace back to the setting that produced it.
  //
  // When it is not applied, the reason is printed rather than the row hidden: a
  // setting switched on and silently dropped is exactly how a diver ends up
  // believing his shallow stops were computed on oxygen when they were not.
  renderO2FlushRow(settings) {
    if (settings.ccrO2FlushEnabled !== true) return ''

    const { segments, ccr } = window.store.state
    const planned = settings.ccrFlushEnabled === true && Array.isArray(ccr.flushes)
      ? ccr.flushes.map(f => f?.depth)
      : []
    const status = describeO2Flush(settings, segments, planned)

    if (!status.active) {
      return `<div class="alert alert-warning py-2 mt-2 text-xs">
        <span>${this.t(`plan_o2_flush_skipped_${status.reason}`, {
        depth: status.depth ?? '-',
        maxDepth: status.maxDepth ?? '-'
      })}</span>
      </div>`
    }

    const figures = flushFigures(status.mix, status.depth, planEnvironment())
    return `<div class="bg-base-200 rounded p-2 mt-2 text-xs">
      <p class="font-bold">${this.t('plan_o2_flush_row', {
      gas: escapeHtml(status.mix.name),
      depth: status.depth,
      time: status.time
    })}</p>
      ${figures
      ? `<p class="opacity-70 mt-1">${this.t('plan_flush_figures', {
        gas: escapeHtml(status.mix.name),
        depth: status.depth,
        ppO2: figures.ppO2.toFixed(2),
        end: Math.round(figures.end),
        density: figures.density.toFixed(1)
      })}</p>`
      : ''}
      <p class="opacity-70 mt-1">${this.t('plan_o2_flush_source')}</p>
    </div>`
  }

  renderSegmentsSection(segments, gases, settings) {
    return `
        <!-- Dive Segments -->
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('plan_segments')}</h2>
              <button id="add-segment-btn" class="btn btn-primary btn-xs">+ ${this.t('add')}</button>
            </div>

            <div id="segments-list" class="space-y-2 mt-2">
              ${segments.length === 0
        ? `<p class="text-sm opacity-70">${this.t('plan_no_segments')}</p>`
        : segments.map((s, i) => this.renderSegmentCard(s, i, gases)).join('')
      }
            </div>
          </div>
        </div>

        ${this.renderFlushesSection(settings)}

        <!-- GF Presets -->
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-3">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-xs font-medium opacity-70">GF:</span>
              ${(settings.gfPresets || []).map(preset => `
                <button class="btn btn-xs gf-preset-btn ${settings.gfLow * 100 === preset.gfLow && settings.gfHigh * 100 === preset.gfHigh ? 'btn-primary' : 'btn-outline'}"
                  data-gf-low="${preset.gfLow}" data-gf-high="${preset.gfHigh}">
                  ${escapeHtml(preset.name)}
                </button>
              `).join('')}
              <button class="btn btn-xs ${this.isCustomGF(settings) ? 'btn-secondary' : 'btn-ghost'}" id="custom-gf-btn">
                ${this.isCustomGF(settings) ? `${Math.round(settings.gfLow * 100)}/${Math.round(settings.gfHigh * 100)}` : this.t('settings_gf_custom')}
              </button>
            </div>
          </div>
        </div>

        <!-- Dive Info (Site, Divers, Notes) -->
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4 space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <div class="form-control">
                <label class="label py-0"><span class="label-text text-xs">${this.t('settings_dive_site')}</span></label>
                <input type="text" id="dive-site"
                  placeholder="${this.t('settings_dive_site_placeholder')}"
                  class="input input-bordered input-sm w-full"
                  value="${settings.diveSite || ''}" />
              </div>
              <div class="form-control">
                <label class="label py-0"><span class="label-text text-xs">${this.t('settings_divers')}</span></label>
                <input type="text" id="dive-divers"
                  placeholder="${this.t('plan_divers_placeholder')}"
                  class="input input-bordered input-sm w-full"
                  value="${settings.diverNames || ''}" />
              </div>
            </div>
            <div class="form-control">
              <label class="label py-0"><span class="label-text text-xs">${this.t('plan_notes')}</span></label>
              <textarea id="dive-notes" rows="2"
                placeholder="${this.t('plan_notes_placeholder')}"
                class="textarea textarea-bordered textarea-sm w-full">${settings.diveNotes || ''}</textarea>
            </div>
          </div>
        </div>

        <!-- Environment Settings (collapsible) -->
        <div class="collapse collapse-arrow bg-base-100 shadow-sm">
          <input type="checkbox" id="env-collapse" ${this.envOpen ? 'checked' : ''} />
          <div class="collapse-title text-sm font-medium py-2 min-h-0">
            ${this.t('settings_environment')}
            <span class="text-xs opacity-70 ml-2">(${window.store.state.environment?.gasTemp ?? 35}°C, ${this.waterLabel()}, ${window.store.state.environment?.altitude ?? 0}m)</span>
          </div>
          <div class="collapse-content">
            <div class="grid grid-cols-3 gap-3 pt-2">
              <div class="form-control">
                <label class="label py-0"><span class="label-text text-xs">${this.t('settings_gas_temp')}</span></label>
                <div class="join">
                  <input type="number" id="plan-gas-temp" value="${window.store.state.environment?.gasTemp ?? 35}" min="20" max="40" step="1"
                    class="input input-bordered input-sm w-full join-item" />
                  <span class="join-item btn btn-sm no-animation">°C</span>
                </div>
              </div>
              <div class="form-control">
                <label class="label py-0"><span class="label-text text-xs">${this.t('settings_water_type')}</span></label>
                <select id="plan-water-type" class="select select-bordered select-sm w-full">
                  ${this.renderWaterOptions(Store.waterPresetKey(this.waterType()))}
                </select>
              </div>
              <div class="form-control">
                <label class="label py-0"><span class="label-text text-xs">${this.t('settings_altitude')}</span></label>
                <div class="join">
                  <input type="number" id="plan-altitude" value="${window.store.state.environment?.altitude ?? 0}" min="0" max="4000" step="100"
                    class="input input-bordered input-sm w-full join-item" />
                  <span class="join-item btn btn-sm no-animation">m</span>
                </div>
              </div>
            </div>

            <!-- Custom water density, shown only when the selector is on "custom" -->
            <div id="plan-water-density-row" class="form-control mt-2 ${Store.waterPresetKey(this.waterType()) === Store.CUSTOM_WATER ? '' : 'hidden'}">
              <label class="label py-0"><span class="label-text text-xs">${this.tChecked('settings_water_density')}</span></label>
              <div class="join">
                <input type="number" id="plan-water-density" value="${this.waterDensity() ?? ''}"
                  min="${Gas.SALINITY_MIN}" max="${Gas.SALINITY_MAX}" step="10"
                  class="input input-bordered input-sm w-full join-item" />
                <span class="join-item btn btn-sm no-animation">g/l</span>
              </div>
              <label class="label py-0">
                <span class="label-text-alt text-xs opacity-70">${this.tChecked('settings_water_density_hint', { min: Gas.SALINITY_MIN, max: Gas.SALINITY_MAX })}</span>
              </label>
              <p id="plan-water-density-error" class="text-xs text-error hidden"></p>
            </div>

            <label class="label py-0">
              <span id="plan-water-figures" class="label-text-alt text-xs opacity-70">${this.formatWaterFigures(this.waterDensity())}</span>
            </label>
          </div>
        </div>

        <!-- An input the plan depends on changed after it was computed: the
             plan was dropped rather than left on screen under settings it does
             not describe, and the diver is told to recalculate it -->
        ${this.renderStaleNotice()}

        ${this.renderDayEditingNotice()}

        <!-- Calculate Button -->
        <button id="calculate-btn" class="btn btn-primary w-full" ${segments.length === 0 ? 'disabled' : ''}>
          ${this.t('calculate')}
        </button>

        <!-- Plan error (filled by applyPlanError, right under the button that
             triggered the failed calculation) -->
        <div id="plan-error" class="hidden"></div>`
  }

  // Results container: config summary, CCR/bailout tables, gas consumption, action buttons
  renderResultsSection(settings, ccr) {
    return `
        <div id="results-container" class="space-y-4 hidden">
          <div class="flex justify-end">
            <button id="fullscreen-results-btn" class="btn btn-ghost btn-xs" title="${this.t('profile_fullscreen') || 'Fullscreen'}">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          </div>
          <!-- Config Summary -->
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body p-4">
              <h2 class="card-title text-sm">${this.t('plan_config')}</h2>
              <div class="grid grid-cols-3 gap-2 text-xs">
                <div>GF: ${Math.round(settings.gfLow * 100)}/${Math.round(settings.gfHigh * 100)}</div>
                <div>${this.t('plan_descent')}: ${settings.descentRate}m/min</div>
                <div>${this.t('plan_ascent')}: ${settings.ascentRate}m/min</div>
                <div>${this.t('settings_deco_ascent')}: ${settings.decoAscentRate}m/min</div>
                <div>${this.t('settings_last_stop_ascent')}: ${settings.lastStopAscentRate}m/min</div>
                <div>${this.t('plan_algorithm')}: ${settings.algorithm.toUpperCase()}</div>
                <div>${this.t('settings_last_stop')}: ${settings.lastStopDepth}m</div>
                <div>${this.t('plan_mode')}: ${ccr.enabled ? 'CCR' : 'OC'}</div>
              </div>
            </div>
          </div>

          <!-- CCR + Bailout grid for fullscreen -->
          <div class="fullscreen-grid">
            <!-- CCR Plan -->
            <div id="ccr-results" class="card bg-base-100 shadow-sm ${ccr.enabled ? '' : 'hidden'}">
              <div class="card-body p-4">
                <h2 class="card-title text-sm">${this.t('plan_ccr')}</h2>
                <div id="ccr-table" class="overflow-x-auto"></div>
                <div id="ccr-summary" class="mt-2"></div>
              </div>
            </div>

            <!-- Bailout Plan (CCR) or OC Plan -->
            <div id="bailout-results" class="card bg-base-100 shadow-sm">
              <div class="card-body p-4">
                <h2 class="card-title text-sm" id="bailout-title">${ccr.enabled ? this.t('plan_bailout') : this.t('plan_deco')}</h2>
                <div id="bailout-table" class="overflow-x-auto"></div>
                <div id="bailout-summary" class="mt-2"></div>
              </div>
            </div>
          </div>

          <!-- Gas Consumption -->
          <div id="gas-consumption" class="card bg-base-100 shadow-sm hidden">
            <div class="card-body p-4">
              <h2 id="gas-consumption-title" class="card-title text-sm">${this.t('plan_gas_requirements')}</h2>
              <div id="gas-table" class="overflow-x-auto"></div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-2 p-2">
            <button id="save-plan-btn" class="btn btn-primary flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              ${this.t('saved_plans_save')}
            </button>
            <button id="export-pdf-btn" class="btn btn-primary flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              ${this.t('plan_export_pdf')}
            </button>
            <button id="share-btn" class="btn btn-primary flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              ${this.t('plan_share')}
            </button>
          </div>
        </div>`
  }

  // Save plan, add/edit segment, and custom GF modal dialogs
  renderModals(ccr, gases, settings) {
    return `
      <!-- Save Plan Modal -->
      <dialog id="save-plan-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('saved_plans_save')}</h3>
          <div class="form-control mt-4">
            <label class="label"><span class="label-text">${this.t('saved_plans_name_label')}</span></label>
            <input type="text" id="save-plan-name" class="input input-bordered" placeholder="${this.t('saved_plans_name_placeholder')}" />
          </div>
          <div class="modal-action">
            <button id="cancel-save-plan-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="confirm-save-plan-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <!-- Add/Edit Segment Modal -->
      <dialog id="segment-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg" id="segment-modal-title">${this.t('plan_add_segment')}</h3>

          <div class="grid grid-cols-2 gap-4 mt-4">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('plan_depth')} (m)</span></label>
              <input type="number" id="segment-depth" min="1" max="200" value="40" class="input input-bordered" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('plan_time')} (min)</span></label>
              <input type="number" id="segment-time" min="1" max="999" value="20" class="input input-bordered" />
            </div>
          </div>

          <!-- OC Gas selection -->
          <div id="oc-gas-section" class="form-control mt-4 ${ccr.enabled ? 'hidden' : ''}">
            <label class="label"><span class="label-text">${this.t('plan_gas')}</span></label>
            <select id="segment-gas" class="select select-bordered w-full">
              ${gases.filter(g => g.enabled).map(g => `
                <option value="${g.id}">${escapeHtml(g.name)}</option>
              `).join('')}
            </select>
          </div>

          <!-- CCR Diluent & Setpoint -->
          <div id="ccr-segment-section" class="${ccr.enabled ? '' : 'hidden'} mt-4 space-y-3">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('plan_diluent')}</span></label>
              <select id="segment-diluent" class="select select-bordered w-full">
                ${window.store.state.diluents.filter(d => d.enabled).map((d) => {
                  const idx = window.store.state.diluents.findIndex(dil => dil.id === d.id)
                  return `<option value="${idx}">${escapeHtml(d.name)} (${Math.round(d.fO2 * 100)}/${Math.round((d.fHe || 0) * 100)})</option>`
                }).join('')}
              </select>
            </div>
            <div class="form-control">
              ${ccr.mode === 'dynamic' ? `
                <div class="alert alert-info text-sm mb-2">
                  <span>${this.t('plan_dynamic_setpoint_info')} (${ccr.dynamicSetpoints.descent}/${ccr.dynamicSetpoints.bottom}/${ccr.dynamicSetpoints.ascent})</span>
                </div>
                <label class="label cursor-pointer justify-start gap-2">
                  <input type="checkbox" id="segment-sp-override" class="checkbox checkbox-sm" />
                  <span class="label-text">${this.t('plan_override_setpoint')}</span>
                </label>
                <div id="segment-sp-override-input" class="hidden">
                  <input type="number" id="segment-setpoint" min="0.7" max="1.6" step="0.1" value="${ccr.fixedSetpoint || 1.3}" class="input input-bordered w-full" />
                </div>
              ` : `
                <label class="label"><span class="label-text">${this.t('plan_setpoint')} (bar)</span></label>
                <input type="number" id="segment-setpoint" min="0.7" max="1.6" step="0.1" value="${ccr.fixedSetpoint || 1.3}" class="input input-bordered w-full" />
              `}
            </div>
          </div>

          <div id="segment-form-error" class="text-error text-sm mt-2 hidden"></div>

          <div class="modal-action">
            <button id="cancel-segment-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="save-segment-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <!-- Custom GF Modal -->
      <dialog id="custom-gf-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('settings_gf_custom')}</h3>
          <div class="grid grid-cols-2 gap-4 mt-4">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('settings_gf_low')} (%)</span></label>
              <input type="number" id="custom-gf-low" min="10" max="100" value="${Math.round(settings.gfLow * 100)}" class="input input-bordered" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('settings_gf_high')} (%)</span></label>
              <input type="number" id="custom-gf-high" min="10" max="100" value="${Math.round(settings.gfHigh * 100)}" class="input input-bordered" />
            </div>
          </div>
          <div class="modal-action">
            <button id="cancel-custom-gf-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="save-custom-gf-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <!-- Flush Depth Helper Modal -->
      <dialog id="flush-depth-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('plan_flush_helper_title')}</h3>
          <p class="text-sm opacity-70 mt-1">${this.t('plan_flush_helper_hint')}</p>

          <p class="text-sm mt-4"><span class="opacity-70">${this.t('plan_flush_helper_mix')} :</span> <span id="flush-helper-mix" class="font-bold"></span></p>

          <div class="grid grid-cols-2 gap-4 mt-2">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('plan_flush_helper_target')}</span></label>
              <select id="flush-helper-kind" class="select select-bordered">
                <option value="ppO2">${this.t('plan_flush_target_ppo2')}</option>
                <option value="end">${this.t('plan_flush_target_end')}</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text" id="flush-helper-unit"></span></label>
              <input type="number" id="flush-helper-value" class="input input-bordered" min="0" step="0.1" />
            </div>
          </div>

          <div id="flush-helper-result" class="mt-4 text-sm"></div>

          <div class="modal-action">
            <button id="cancel-flush-helper-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="apply-flush-helper-btn" class="btn btn-primary">${this.t('plan_flush_helper_apply')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <!-- PDF Format Choice Modal -->
      <dialog id="pdf-format-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('pdf_choose_format')}</h3>
          <p class="text-sm opacity-70 mt-1">${this.t('pdf_choose_format_hint')}</p>
          <div class="flex flex-col gap-3 mt-4">
            <button id="pdf-format-complete-btn" class="btn btn-primary flex-col h-auto py-3 items-start normal-case">
              <span class="font-bold">${this.t('pdf_format_complete')}</span>
              <span class="text-xs font-normal opacity-80">${this.t('pdf_format_complete_hint')}</span>
            </button>
            <button id="pdf-format-condensed-btn" class="btn btn-secondary flex-col h-auto py-3 items-start normal-case">
              <span class="font-bold">${this.t('pdf_format_condensed')}</span>
              <span class="text-xs font-normal opacity-80">${this.t('pdf_format_condensed_hint')}</span>
            </button>
          </div>
          <div class="modal-action">
            <button id="cancel-pdf-format-btn" class="btn btn-ghost">${this.t('cancel')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>`
  }

  renderSegmentCard(segment, index, gases) {
    // The gas the segment is set to, and only that one: the engine plans the
    // segment on it (DayPlanner.resolveSegmentGases) and refuses it when it is
    // disabled or gone, so the card must not print a stand-in the plan below
    // would not be computed on. No gasId means the engine picks by MOD.
    const gas = gases.find(g => g.id === segment.gasId)
    const { ccr, diluents } = window.store.state

    // Get diluent info for CCR
    let ccrInfo = ''
    if (ccr.enabled) {
      const diluent = segment.diluentIndex !== undefined
        ? diluents[segment.diluentIndex]
        : diluents.find(d => d.enabled) || diluents[0]
      let setpointDisplay
      if (segment.setpoint !== undefined) {
        setpointDisplay = `SP${segment.setpoint}`
      } else if (ccr.mode === 'dynamic') {
        setpointDisplay = `SP ${ccr.dynamicSetpoints.descent}/${ccr.dynamicSetpoints.bottom}/${ccr.dynamicSetpoints.ascent}`
      } else {
        setpointDisplay = `SP${ccr.fixedSetpoint}`
      }
      ccrInfo = `<div class="text-xs opacity-70">${escapeHtml(diluent?.name || 'Air')} @ ${setpointDisplay}</div>`
    }

    return `
      <div class="flex items-center justify-between p-3 bg-base-200 rounded-lg">
        <div class="flex items-center gap-3">
          <div class="badge badge-primary">${index + 1}</div>
          <div>
            <div class="font-medium">${segment.depth}m ${this.t('plan_time').toLowerCase()} ${segment.time} min</div>
            ${ccr.enabled ? ccrInfo : `<div class="text-xs opacity-70">${this.t('plan_gas')}: ${gas ? escapeHtml(gas.name) : (segment.gasId === undefined ? 'Auto' : '?')}</div>`}
          </div>
        </div>
        <div class="flex gap-1">
          <button class="btn btn-ghost btn-xs edit-segment-btn" data-index="${index}">${this.t('edit')}</button>
          <button class="btn btn-ghost btn-xs text-error delete-segment-btn" data-index="${index}">X</button>
        </div>
      </div>
    `
  }

  // Current loop flushes, always an array so the callers never guard twice
  flushes() {
    const list = window.store.state.ccr?.flushes
    return Array.isArray(list) ? list : []
  }

  // Write the flush list back and redraw. The whole list goes through
  // store.update('ccr.flushes', ...): the path is declared a plan input, so the
  // computed plan is dropped exactly as it is for a segment or a gas.
  writeFlushes(flushes) {
    window.store.update('ccr.flushes', flushes)
    this.render()
  }

  // Read one field of one flush row, keeping an emptied field out of the store.
  //
  // An <input type="number"> emptied by the diver yields the empty string, and
  // Number('') is 0: a cleared depth would be written as a flush at the
  // surface, a cleared duration as a flush of no duration. Both are refused by
  // the engine, but only after the diver has lost what he typed, so the empty
  // string is kept as NaN and simply not written.
  updateFlushField(index, field, raw) {
    const flushes = this.flushes()
    if (!flushes[index]) {
      console.error('PlanView: flush #%o no longer exists, %s not written', index, field)
      return
    }
    const value = raw === '' ? Number.NaN : Number(raw)
    if (!Number.isFinite(value) || value <= 0) {
      console.error('PlanView: flush #%d %s = %o refused, expected a number above 0', index, field, raw)
      return
    }
    const next = flushes.map((flush, i) => (i === index ? { ...flush, [field]: value } : flush))
    window.store.update('ccr.flushes', next)
  }

  // Fill the helper dialog from its own fields. Called on every keystroke, so
  // it reads the dialog and writes the dialog, never the store: the plan is
  // only touched when the diver applies the depth.
  refreshFlushHelper() {
    const box = document.getElementById('flush-helper-result')
    const apply = document.getElementById('apply-flush-helper-btn')
    if (!box || !apply) return

    const flush = this.flushes()[this.flushHelperIndex]
    const mix = window.store.state.diluents[flush?.diluentIndex]
    const kind = document.getElementById('flush-helper-kind')?.value === 'end' ? 'end' : 'ppO2'
    const raw = document.getElementById('flush-helper-value')?.value
    const target = raw === '' ? Number.NaN : Number(raw)

    const unit = document.getElementById('flush-helper-unit')
    if (unit) unit.textContent = this.t(kind === 'end' ? 'plan_flush_target_end' : 'plan_flush_target_ppo2')

    const { depth, exact, figures, reason } = flushDepthForTarget(mix, kind, target, planEnvironment())
    this.flushHelperResult = depth
    apply.disabled = depth === null

    if (depth === null) {
      box.innerHTML = reason
        ? `<p class="text-error">${this.t(reason === 'no_nitrogen' ? 'plan_flush_helper_no_nitrogen' : 'plan_flush_helper_unreachable')}</p>`
        : ''
      return
    }

    // The bottom is the dive's, so the refusal the engine would raise later is
    // said here instead, while the diver is still choosing his target.
    const maxDepth = Math.max(0, ...window.store.state.segments.map(s => s.depth || 0))
    const notes = []
    if (depth >= maxDepth) {
      apply.disabled = true
      notes.push(`<p class="text-error">${this.t('plan_flush_helper_deeper', { maxDepth })}</p>`)
    }

    // Same band as the engine's own flush warning, so the helper cannot propose
    // a depth the computed plan will immediately complain about: the deco limit
    // and the MOD of the mix, tolerance included, not a bare ppO2 comparison.
    const settings = window.store.state.settings
    const limit = Gas.ppO2LimitOf(mix, settings.ppO2Deco)
    const mod = Gas.modOf(mix, settings.ppO2Deco, planEnvironment())
    if (depth > mod || figures.ppO2 < DivePlanner.HYPOXIC_PPO2) {
      notes.push(`<p class="text-warning">${this.t('plan_flush_helper_band', {
        min: DivePlanner.HYPOXIC_PPO2.toFixed(2),
        max: Number(limit).toFixed(2)
      })}</p>`)
    }

    box.innerHTML = `
      <p class="font-bold">${this.t('plan_flush_helper_result', { depth, exact: exact.toFixed(1) })}</p>
      <p class="opacity-70">${this.t('plan_flush_figures', {
        gas: mix.name,
        depth,
        ppO2: figures.ppO2.toFixed(2),
        end: Math.round(figures.end),
        density: figures.density.toFixed(1)
      })}</p>
      <p class="text-xs opacity-70 mt-1">${this.t('plan_flush_helper_rounding')}</p>
      ${notes.join('')}`
  }

  // Open the helper on one flush row, with the ppO2 the diver already allows
  // himself as the first target: the depth it lands on is the deepest this mix
  // is planned at anywhere else in the application.
  openFlushHelper(index) {
    const flush = this.flushes()[index]
    const mix = window.store.state.diluents[flush?.diluentIndex]
    if (!flush || !mix) {
      console.error('PlanView: flush #%o has no mix to compute a depth from (%o)', index, flush)
      return
    }
    const modal = document.getElementById('flush-depth-modal')
    const mixLabel = document.getElementById('flush-helper-mix')
    const kind = document.getElementById('flush-helper-kind')
    const value = document.getElementById('flush-helper-value')
    if (!modal || !mixLabel || !kind || !value) {
      console.error('PlanView: the flush depth dialog is not in the page, nothing to open')
      return
    }
    this.flushHelperIndex = index
    this.flushHelperResult = null
    mixLabel.textContent = mix.name
    kind.value = 'ppO2'
    value.value = Gas.ppO2LimitOf(mix, window.store.state.settings.ppO2Deco)
    this.refreshFlushHelper()
    modal.showModal()
  }

  // Bind the loop flush editor. Separate from bindEvents only because the
  // section is optional: nothing here exists on an open circuit dive.
  bindFlushEvents() {
    document.getElementById('add-flush-btn')?.addEventListener('click', () => {
      const { diluents, settings, segments } = window.store.state
      const firstEnabled = diluents.findIndex(d => d.enabled)
      if (firstEnabled < 0) {
        console.error('PlanView: no diluent is enabled, a flush has no mix to be made with')
        return
      }
      // Halfway up from the deepest level, snapped to a whole metre: a depth
      // the engine accepts, that the diver then moves where he wants. Proposing
      // the bottom itself would be refused, a flush happens on the way up.
      const maxDepth = Math.max(0, ...segments.map(s => s.depth || 0))
      const proposed = Math.max(1, Math.round(maxDepth / 2))
      this.writeFlushes([...this.flushes(), {
        depth: proposed,
        diluentIndex: firstEnabled,
        time: settings.ccrFlushDuration > 0 ? settings.ccrFlushDuration : 1,
        // Written rather than left out: the row shows an unticked box, and the
        // stored flush has to say the same thing as the box.
        keepMix: false
      }])
    })

    this.container.querySelectorAll('.flush-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.currentTarget.dataset.flushIndex)
        this.writeFlushes(this.flushes().filter((_, i) => i !== index))
      })
    })

    // 'change' and not 'input': every keystroke would write the store, the
    // store would notify, and the re-render would take the field away from
    // under the diver while he is still typing his depth.
    this.container.querySelectorAll('.flush-depth').forEach(input => {
      input.addEventListener('change', (e) => {
        this.updateFlushField(parseInt(e.currentTarget.dataset.flushIndex), 'depth', e.currentTarget.value)
      })
    })

    this.container.querySelectorAll('.flush-time').forEach(input => {
      input.addEventListener('change', (e) => {
        this.updateFlushField(parseInt(e.currentTarget.dataset.flushIndex), 'time', e.currentTarget.value)
      })
    })

    this.container.querySelectorAll('.flush-helper').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.openFlushHelper(parseInt(e.currentTarget.dataset.flushIndex))
      })
    })

    // 'input' and not 'change' here, unlike the rows: nothing is written to the
    // store while the dialog is open, so recomputing on every keystroke costs
    // nothing and lets the diver watch the depth move as he types.
    document.getElementById('flush-helper-value')?.addEventListener('input', () => this.refreshFlushHelper())
    document.getElementById('flush-helper-kind')?.addEventListener('change', () => this.refreshFlushHelper())

    document.getElementById('cancel-flush-helper-btn')?.addEventListener('click', () => {
      document.getElementById('flush-depth-modal')?.close()
    })

    document.getElementById('apply-flush-helper-btn')?.addEventListener('click', () => {
      const depth = this.flushHelperResult
      const index = this.flushHelperIndex
      if (!Number.isFinite(depth) || depth < 1) {
        console.error('PlanView: no usable depth to apply to flush #%o (%o)', index, depth)
        return
      }
      // Closed first: the write notifies the store, and a re-render is skipped
      // while a dialog of this view is open, so the row would keep the old
      // depth on screen until something else redrew it.
      document.getElementById('flush-depth-modal')?.close()
      this.updateFlushField(index, 'depth', String(depth))
      this.render()
    })

    // The tick decides what the loop breathes above the flush, so the plan on
    // screen is no longer the one that was computed: the write goes through
    // store.update like every other flush field, which drops it.
    this.container.querySelectorAll('.flush-keep').forEach(box => {
      box.addEventListener('change', (e) => {
        const index = parseInt(e.currentTarget.dataset.flushIndex)
        const flushes = this.flushes()
        if (!flushes[index]) {
          console.error('PlanView: flush #%o no longer exists, keepMix not written', index)
          return
        }
        const keepMix = e.currentTarget.checked === true
        window.store.update('ccr.flushes', flushes.map((flush, i) => (i === index ? { ...flush, keepMix } : flush)))
      })
    })

    this.container.querySelectorAll('.flush-diluent').forEach(select => {
      select.addEventListener('change', (e) => {
        const index = parseInt(e.currentTarget.dataset.flushIndex)
        const diluentIndex = parseInt(e.currentTarget.value)
        if (!Number.isInteger(diluentIndex) || !window.store.state.diluents[diluentIndex]) {
          console.error('PlanView: flush #%d points at diluent #%o, which does not exist', index, e.currentTarget.value)
          return
        }
        const next = this.flushes().map((flush, i) => (i === index ? { ...flush, diluentIndex } : flush))
        window.store.update('ccr.flushes', next)
      })
    })
  }

  // Attach event listeners for segments CRUD, GF presets, calculation, export, and environment
  bindEvents() {
    this.bindFlushEvents()

    // Add segment
    document.getElementById('add-segment-btn')?.addEventListener('click', () => {
      this.openSegmentModal(null)
    })

    // Edit segment
    this.container.querySelectorAll('.edit-segment-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.openSegmentModal(parseInt(e.target.dataset.index))
      })
    })

    // Delete segment
    this.container.querySelectorAll('.delete-segment-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index)
        window.store.removeSegment(index)
        this.render()
      })
    })

    // Save segment
    document.getElementById('save-segment-btn')?.addEventListener('click', () => {
      this.saveSegment()
    })

    // Cancel segment
    document.getElementById('cancel-segment-btn')?.addEventListener('click', () => {
      document.getElementById('segment-modal').close()
    })

    // Setpoint override checkbox (dynamic mode)
    document.getElementById('segment-sp-override')?.addEventListener('change', (e) => {
      const inputDiv = document.getElementById('segment-sp-override-input')
      if (inputDiv) {
        inputDiv.classList.toggle('hidden', !e.target.checked)
      }
    })

    // GF preset buttons
    this.container.querySelectorAll('.gf-preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const gfLow = parseInt(btn.dataset.gfLow) / 100
        const gfHigh = parseInt(btn.dataset.gfHigh) / 100
        window.store.update('settings.gfLow', gfLow)
        window.store.update('settings.gfHigh', gfHigh)
        this.render()
      })
    })

    // Custom GF button
    document.getElementById('custom-gf-btn')?.addEventListener('click', () => {
      document.getElementById('custom-gf-low').value = Math.round(window.store.state.settings.gfLow * 100)
      document.getElementById('custom-gf-high').value = Math.round(window.store.state.settings.gfHigh * 100)
      document.getElementById('custom-gf-modal')?.showModal()
    })

    // Cancel custom GF
    document.getElementById('cancel-custom-gf-btn')?.addEventListener('click', () => {
      document.getElementById('custom-gf-modal')?.close()
    })

    // Save custom GF
    document.getElementById('save-custom-gf-btn')?.addEventListener('click', () => {
      const gfLow = parseInt(document.getElementById('custom-gf-low').value) / 100
      const gfHigh = parseInt(document.getElementById('custom-gf-high').value) / 100
      window.store.update('settings.gfLow', gfLow)
      window.store.update('settings.gfHigh', gfHigh)
      document.getElementById('custom-gf-modal')?.close()
      this.render()
    })

    // Calculate
    document.getElementById('calculate-btn')?.addEventListener('click', () => {
      this.calculate()
    })

    // Dive of the day open on this screen: save it back into the series, or
    // detach it and keep it here as an ordinary dive
    document.getElementById('day-save-dive-btn')?.addEventListener('click', () => {
      if (window.store.saveEditedDive()) {
        window.location.hash = '/day'
      }
      // Store.saveEditedDive logged the reason when it refused
    })

    document.getElementById('day-detach-dive-btn')?.addEventListener('click', () => {
      window.store.stopEditingDive()
      this.render()
    })

    // Fullscreen results
    document.getElementById('fullscreen-results-btn')?.addEventListener('click', () => {
      toggleFullscreen('results-container')
    })

    // Export PDF - open format choice popup
    document.getElementById('export-pdf-btn')?.addEventListener('click', () => {
      this.openPdfFormatModal()
    })

    // PDF format choice
    document.getElementById('pdf-format-complete-btn')?.addEventListener('click', () => {
      document.getElementById('pdf-format-modal')?.close()
      this.exportPdf('complete')
    })
    document.getElementById('pdf-format-condensed-btn')?.addEventListener('click', () => {
      document.getElementById('pdf-format-modal')?.close()
      this.exportPdf('condensed')
    })
    document.getElementById('cancel-pdf-format-btn')?.addEventListener('click', () => {
      document.getElementById('pdf-format-modal')?.close()
    })

    // Save plan
    document.getElementById('save-plan-btn')?.addEventListener('click', () => {
      this.openSavePlanModal()
    })

    document.getElementById('cancel-save-plan-btn')?.addEventListener('click', () => {
      document.getElementById('save-plan-modal')?.close()
    })

    document.getElementById('confirm-save-plan-btn')?.addEventListener('click', () => {
      this.savePlan()
    })

    // Share
    document.getElementById('share-btn')?.addEventListener('click', () => {
      this.sharePlan()
    })

    // Dive info (site, divers, notes)
    document.getElementById('dive-site')?.addEventListener('input', (e) => {
      window.store.update('settings.diveSite', e.target.value)
    })

    document.getElementById('dive-divers')?.addEventListener('input', (e) => {
      window.store.update('settings.diverNames', e.target.value)
    })

    document.getElementById('dive-notes')?.addEventListener('input', (e) => {
      window.store.update('settings.diveNotes', e.target.value)
    })

    // Environment panel open state, restored on the next render so a store
    // update triggered from inside the panel does not fold it back
    document.getElementById('env-collapse')?.addEventListener('change', (e) => {
      this.envOpen = e.target.checked
    })

    // Environment settings (per plan). Every write goes through
    // updateEnvironment so a change that makes the computed plan obsolete
    // drops it instead of leaving it on screen.
    document.getElementById('plan-gas-temp')?.addEventListener('change', (e) => {
      this.updateEnvironment('gasTemp', parseInt(e.target.value))
      this.render()
    })

    document.getElementById('plan-water-type')?.addEventListener('change', (e) => {
      this.selectWaterType(e.target.value)
    })

    // Typing is followed live so the metres per bar and the refusal of an out
    // of range value are immediate, but only a settled value (change: blur or
    // Enter) is written to the store.
    const densityInput = document.getElementById('plan-water-density')
    densityInput?.addEventListener('input', () => this.applyDensityInput(false))
    densityInput?.addEventListener('change', () => this.applyDensityInput(true))

    document.getElementById('plan-altitude')?.addEventListener('change', (e) => {
      // Refused rather than stored: an emptied field gives NaN, which the
      // engine used to read as sea level without a word. Restoring the field
      // to the stored value tells the diver the entry was not taken.
      const altitude = parseInt(e.target.value, 10)
      if (!Store.isUsableAltitude(altitude)) {
        console.error('PlanView: rejected altitude %o, keeping %o m',
          e.target.value, this.environment().altitude ?? 0)
        e.target.value = this.environment().altitude ?? 0
        return
      }
      this.updateEnvironment('altitude', altitude)
      this.render()
    })
  }

  // Apply a choice made in the water selector.
  // A named preset is stored as its key, which is what every state written
  // before this screen offered a density already contains. "Custom" stores the
  // density of the water currently selected: the same number the engine was
  // already using, so switching to custom never changes the plan by itself.
  selectWaterType(choice) {
    if (choice === Store.CUSTOM_WATER) {
      let density = this.waterDensity()
      if (density === null) {
        // The stored value is unusable (already logged): start the custom field
        // from sea water rather than leave the diver with an empty field.
        density = Gas.SEAWATER_SALINITY
        console.error('PlanView: stored water type %o unusable, starting the custom density at %o g/l',
          this.waterType(), density)
      }
      this.updateEnvironment('waterType', density)
      this.render()
      return
    }

    if (!Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, choice)) {
      console.error('PlanView: unknown water preset "%s" selected, environment left unchanged', choice)
      return
    }

    this.updateEnvironment('waterType', choice)
    this.render()
  }

  // Read the custom density field, tell the diver whether it is usable, and
  // write it to the store only when it is. `commit` is false while typing
  // (input event) and true once the value is settled (change event).
  // An out of range value is refused and displayed as such: nothing is written,
  // and the rest of the screen keeps working - the whole point of validating
  // here instead of letting Gas.waterDensity throw from inside a handler.
  applyDensityInput(commit) {
    const input = document.getElementById('plan-water-density')
    if (!input) {
      console.error('PlanView: #plan-water-density is missing, the custom density cannot be read')
      return
    }

    const raw = input.value.trim()
    const density = raw === '' ? NaN : Number(raw)
    const usable = Store.isUsableDensity(density)

    this.showDensityError(!usable)
    this.updateWaterFigures(usable ? density : null)

    if (!usable) {
      // Half typed values are expected while the diver types, hence the warning
      // level; a settled value that is still refused is an error.
      const reason = 'PlanView: water density %o refused, expected a number within [%o, %o] g/l'
      if (commit) {
        console.error(reason, input.value, Gas.SALINITY_MIN, Gas.SALINITY_MAX)
      } else {
        console.warn(reason, input.value, Gas.SALINITY_MIN, Gas.SALINITY_MAX)
      }
      return
    }

    if (!commit) return
    // Writing to the store notifies it, which re-renders this whole view: the
    // density field, the figures line and the collapsed title are rebuilt from
    // the new environment, so nothing else has to be touched here.
    this.updateEnvironment('waterType', density)
  }

  // Show or hide the refusal message under the density field.
  showDensityError(show) {
    const message = document.getElementById('plan-water-density-error')
    const input = document.getElementById('plan-water-density')
    if (!message || !input) {
      console.error('PlanView: #plan-water-density-error or #plan-water-density is missing, cannot report the density')
      return
    }
    // Bounds only, no sentence: the translated hint right above already says
    // what the field expects, and this line has to stay true in six languages.
    message.textContent = `${Gas.SALINITY_MIN} - ${Gas.SALINITY_MAX} g/l`
    message.classList.toggle('hidden', !show)
    input.classList.toggle('input-error', show)
  }

  // Refresh the "1030 g/l - 9.90 m/bar" line under the water selector.
  updateWaterFigures(density) {
    const figures = document.getElementById('plan-water-figures')
    if (!figures) {
      console.error('PlanView: #plan-water-figures is missing, cannot show the water column figures')
      return
    }
    figures.textContent = this.formatWaterFigures(density)
  }

  // Open the format-choice popup (full report vs condensed 1-page sheet)
  openPdfFormatModal() {
    if (!this.results) {
      alert(this.t('plan_calculate_first'))
      return
    }
    document.getElementById('pdf-format-modal')?.showModal()
  }

  async exportPdf(format = 'complete') {
    if (!this.results) {
      alert(this.t('plan_calculate_first'))
      return
    }

    showToast(this.t('pdf_exporting'))
    const { settings, ccr, environment } = window.store.state
    const ccrForExport = this.results?.ccr?.diluent ? { ...ccr, diluent: this.results.ccr.diluent } : ccr
    const bailoutGases = window.store.getBailoutGases()
    const exporter = new PdfExport(this.results, settings, ccrForExport, (key, params) => this.t(key, params), bailoutGases, environment)
    await exporter.exportToPdf(format)
  }

  openSavePlanModal() {
    if (!this.results) {
      alert(this.t('plan_calculate_first'))
      return
    }
    // Auto-generate suggested name
    const state = window.store.state
    const maxDepth = Math.max(...state.segments.map(s => s.depth))
    const totalTime = state.segments.reduce((sum, s) => sum + s.time, 0)
    const suggestedName = `${maxDepth}m/${totalTime}min`

    document.getElementById('save-plan-name').value = suggestedName
    document.getElementById('save-plan-modal')?.showModal()
  }

  savePlan() {
    if (!this.results) return

    const name = document.getElementById('save-plan-name')?.value
    SavedPlans.save(name, window.store.state, this.results)
    document.getElementById('save-plan-modal')?.close()

    // Show confirmation
    const btn = document.getElementById('save-plan-btn')
    if (btn) {
      const originalText = btn.innerHTML
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg> ${this.t('saved_plans_saved')}`
      btn.classList.add('btn-disabled')
      setTimeout(() => {
        btn.innerHTML = originalText
        btn.classList.remove('btn-disabled')
      }, 2000)
    }
  }

  async sharePlan() {
    if (!this.results) {
      alert(this.t('plan_calculate_first'))
      return
    }

    showToast(this.t('pdf_exporting'))
    const { settings, ccr, environment } = window.store.state
    const ccrForExport = this.results?.ccr?.diluent ? { ...ccr, diluent: this.results.ccr.diluent } : ccr
    const bailoutGases = window.store.getBailoutGases()
    const exporter = new PdfExport(this.results, settings, ccrForExport, (key, params) => this.t(key, params), bailoutGases, environment)

    // Native platform (Capacitor): use Filesystem + Share plugins
    if (window.Capacitor?.isNativePlatform?.()) {
      try {
        await exporter.shareNative(this.t('pdf_title'), this.getShareText())
      } catch (e) {
        console.error('Native share failed:', e)
        alert('Share error: ' + e.message)
      }
      return
    }

    // Web: use Web Share API with file support
    if (navigator.share && navigator.canShare) {
      try {
        const pdfBlob = exporter.getPdfBlob()
        const pdfFile = new File([pdfBlob], `dive-plan-${new Date().toISOString().split('T')[0]}.pdf`, { type: 'application/pdf' })

        if (navigator.canShare({ files: [pdfFile] })) {
          await navigator.share({
            title: this.t('pdf_title'),
            text: this.getShareText(),
            files: [pdfFile]
          })
          return
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          return // User cancelled
        }
        console.warn('File share failed, trying text share:', e)
      }
    }

    // Fallback: share text + offer to download PDF
    if (navigator.share) {
      try {
        await navigator.share({
          title: this.t('pdf_title'),
          text: this.getShareText() + '\n\n' + this.t('pdf_footer_generated') + ' - https://mvplan.org'
        })
        await exporter.exportToPdf()
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.warn('Text share failed, falling back to PDF export:', e)
          await exporter.exportToPdf()
        }
      }
    } else {
      await exporter.exportToPdf()
    }
  }

  getShareText() {
    const plan = this.results?.ccr || this.results?.bailout
    if (!plan) return ''

    const state = window.store.state
    const maxDepth = Math.max(...state.segments.map(s => s.depth))
    const mode = state.ccr.enabled ? 'CCR' : 'OC'
    const gf = `${Math.round(state.settings.gfLow * 100)}/${Math.round(state.settings.gfHigh * 100)}`

    // Same figure as the summary cell: on a repetitive dive the CNS shared is
    // the one of the series
    const { cns } = exposureShown(plan)

    return `Dive Plan: ${maxDepth}m ${mode}\nRuntime: ${Math.ceil(plan.runtime)} min | TTS: ${plan.tts} min\nGF: ${gf} | CNS: ${cns}%`
  }

  openSegmentModal(index) {
    const modal = document.getElementById('segment-modal')
    const { ccr, diluents } = window.store.state
    this.editingSegmentIndex = index

    const { gases } = window.store.state
    const enabledGases = gases.filter(g => g.enabled)
    const firstEnabledGasId = enabledGases.length > 0 ? enabledGases[0].id : null

    if (index !== null) {
      const segment = window.store.state.segments[index]
      document.getElementById('segment-modal-title').textContent = this.t('plan_edit_segment')
      document.getElementById('segment-depth').value = segment.depth
      document.getElementById('segment-time').value = segment.time
      // Use segment gas if still enabled, otherwise fallback to first enabled gas
      const segmentGas = gases.find(g => g.id === segment.gasId && g.enabled)
      document.getElementById('segment-gas').value = segmentGas ? segmentGas.id : firstEnabledGasId
      // CCR fields - find the diluent in the enabled list
      const enabledDiluents = diluents.filter(d => d.enabled)
      if (segment.diluentIndex !== undefined && diluents[segment.diluentIndex]) {
        // Find if this diluent is in the enabled list
        const diluentId = diluents[segment.diluentIndex].id
        const enabledIdx = enabledDiluents.findIndex(d => d.id === diluentId)
        if (enabledIdx >= 0) {
          document.getElementById('segment-diluent').value = segment.diluentIndex
        } else {
          // Diluent was disabled, use first enabled
          const firstEnabledIdx = diluents.findIndex(d => d.enabled)
          document.getElementById('segment-diluent').value = firstEnabledIdx >= 0 ? firstEnabledIdx : 0
        }
      } else {
        // Default to first enabled diluent
        const firstEnabledIdx = diluents.findIndex(d => d.enabled)
        document.getElementById('segment-diluent').value = firstEnabledIdx >= 0 ? firstEnabledIdx : 0
      }
      // Setpoint: in dynamic mode, use checkbox to toggle override
      if (ccr.mode === 'dynamic') {
        const hasOverride = segment.setpoint !== undefined
        const checkbox = document.getElementById('segment-sp-override')
        const inputDiv = document.getElementById('segment-sp-override-input')
        if (checkbox) checkbox.checked = hasOverride
        if (inputDiv) inputDiv.classList.toggle('hidden', !hasOverride)
        document.getElementById('segment-setpoint').value = hasOverride ? segment.setpoint : (ccr.fixedSetpoint || 1.3)
      } else {
        document.getElementById('segment-setpoint').value = segment.setpoint !== undefined ? segment.setpoint : (ccr.fixedSetpoint || 1.3)
      }
    } else {
      document.getElementById('segment-modal-title').textContent = this.t('plan_add_segment')
      document.getElementById('segment-depth').value = 40
      document.getElementById('segment-time').value = 20
      document.getElementById('segment-gas').value = firstEnabledGasId
      // CCR defaults - use first enabled diluent
      const firstEnabledIdx = diluents.findIndex(d => d.enabled)
      document.getElementById('segment-diluent').value = firstEnabledIdx >= 0 ? firstEnabledIdx : 0
      // Setpoint: in dynamic mode start unchecked (auto), in fixed mode use configured value
      if (ccr.mode === 'dynamic') {
        const checkbox = document.getElementById('segment-sp-override')
        const inputDiv = document.getElementById('segment-sp-override-input')
        if (checkbox) checkbox.checked = false
        if (inputDiv) inputDiv.classList.add('hidden')
        document.getElementById('segment-setpoint').value = ccr.fixedSetpoint || 1.3
      } else {
        document.getElementById('segment-setpoint').value = ccr.fixedSetpoint || 1.3
      }
    }

    modal.showModal()
  }

  saveSegment() {
    const { ccr } = window.store.state
    // Bounded by the min / max of the fields: an emptied depth or time used to
    // be written as NaN and refused at calculation with a generic message
    const errorEl = document.getElementById('segment-form-error')
    const refuse = () => {
      if (errorEl) {
        errorEl.textContent = this.t('plan_segment_invalid')
        errorEl.classList.remove('hidden')
      }
    }
    errorEl?.classList.add('hidden')
    const depth = readBoundedNumber(document.getElementById('segment-depth'), { integer: true, context: 'PlanView segment' })
    const time = readBoundedNumber(document.getElementById('segment-time'), { integer: true, context: 'PlanView segment' })
    if (depth === null || time === null) return refuse()
    const gasId = parseInt(document.getElementById('segment-gas').value)

    const segmentData = { depth, time, gasId }

    // Add CCR-specific data if in CCR mode
    if (ccr.enabled) {
      segmentData.diluentIndex = parseInt(document.getElementById('segment-diluent').value)
      const readSetpoint = () => readBoundedNumber(document.getElementById('segment-setpoint'), { context: 'PlanView segment' })
      // In dynamic mode, use checkbox to determine if override is enabled
      if (ccr.mode === 'dynamic') {
        const overrideCheckbox = document.getElementById('segment-sp-override')
        if (overrideCheckbox && overrideCheckbox.checked) {
          const setpoint = readSetpoint()
          if (setpoint === null) return refuse()
          segmentData.setpoint = setpoint
        } else {
          // Explicitly remove setpoint override when checkbox is unchecked
          segmentData.setpoint = undefined
        }
      } else {
        // Fixed mode: always save the setpoint value
        const setpoint = readSetpoint()
        if (setpoint === null) return refuse()
        segmentData.setpoint = setpoint
      }
    }

    if (this.editingSegmentIndex !== null) {
      window.store.updateSegment(this.editingSegmentIndex, segmentData)
    } else {
      window.store.addSegment(segmentData)
    }

    document.getElementById('segment-modal').close()
    this.render()
  }

  // Dive environment (altitude / water type / gas temperature) of the plan.
  // Single accessor so the planner, the CCR loop and the alerts all read the
  // very settings the Environment panel writes into the store.
  environment() {
    return window.store.state.environment || {}
  }

  // Create a DivePlanner instance from current settings.
  //
  // Delegates to DayPlanner, the single bridge between the stored state and the
  // engine: the plan of a dive has to be the same whether it is computed here,
  // alone, or as one dive of a day (src/core/DayPlanner.js).
  createPlanner(settings) {
    return createPlanner(settings, this.environment())
  }

  // Adjust segment times when descent time is included in bottom time
  adjustSegmentsForDescent(segments, settings) {
    return adjustSegmentsForDescent(segments, settings)
  }

  // Build CCR config: resolve diluents, prepare segments, create CCR instance.
  // Returns { segmentsWithDiluents, diluent, ccrConfig }
  buildCCRConfig(adjustedSegments, segments, diluents, ccr) {
    return buildCCRConfig(adjustedSegments, segments, diluents, ccr, this.environment(),
      window.store.state.settings || {})
  }

  // Run school mode: recalculate plans with each selected GF preset for comparison
  computeSchoolModeResults(settings, ccr, enabledGases, adjustedSegments, ccrContext, previousDive = null) {
    if (!settings.schoolMode || !settings.gfPresets || !settings.schoolModePresets?.length) return null

    const results = []
    for (const presetIdx of settings.schoolModePresets) {
      const preset = settings.gfPresets[presetIdx]
      if (!preset) continue
      const gfLow = preset.gfLow / 100
      const gfHigh = preset.gfHigh / 100
      if (gfLow === settings.gfLow && gfHigh === settings.gfHigh) continue

      const compPlanner = this.createPlanner(settings)
      try {
        let compPlan
        if (ccr.enabled && ccrContext) {
          compPlan = compPlanner.planCCR({
            segments: ccrContext.segmentsWithDiluents,
            ccr: ccrContext.ccrConfig,
            diluent: ccrContext.diluent,
            bailoutGases: enabledGases,
            flushes: ccrContext.flushes,
            gfLow, gfHigh,
            previousDive
          })
        } else {
          compPlan = compPlanner.planOC({
            segments: adjustedSegments,
            gases: enabledGases,
            gfLow, gfHigh,
            rmvBottom: settings.rmvBottom,
            rmvDeco: settings.rmvDeco,
            previousDive
          })
        }
        results.push({
          preset: preset.name,
          gfLow: preset.gfLow,
          gfHigh: preset.gfHigh,
          segments: compPlan.segments,
          stops: compPlan.stops,
          tts: compPlan.tts,
          runtime: compPlan.runtime
        })
      } catch (e) {
        console.error(`School mode: failed for preset ${preset.name}:`, e)
      }
    }
    return results.length > 0 ? results : null
  }

  // Main calculation orchestrator: creates planner, runs CCR or OC plan,
  // renders results, then runs school mode comparison if enabled.
  //
  // The whole computation is guarded: the engine refuses an inconsistent plan
  // (gradient factors reversed or empty, no active bailout gas, aberrant
  // setpoint...) by throwing a PlanError. Unguarded, the click on Calculate
  // did strictly nothing visible and left the previous - now wrong - plan on
  // screen, which is the worst possible outcome for a dive plan.
  calculate() {
    this.planError = null
    this.applyPlanError()

    try {
      this.computePlan()
    } catch (e) {
      if (e instanceof PlanError) {
        console.error('PlanView.calculate: plan refused by the engine (%s) %o', e.code, e.details)
      } else {
        console.error('PlanView.calculate: unexpected failure while computing the plan:', e)
      }
      // Drop the obsolete plan first (screen and store), then display why
      this.clearResultsDisplay()
      window.store.setResults(null, null, null)
      this.showPlanError(e)
    }
  }

  // Run the plan itself. Every failure path throws (PlanError from the engine)
  // and is handled by calculate().
  // Series context of the dive shown here.
  //
  // When this screen holds a dive of the day, that dive starts from the tissues
  // the previous one left - and the plan on screen has to say so. Computing it
  // as a first dive of the day would show less decompression than the series
  // owes, and the diver would then save that plan into a series it does not
  // describe.
  //
  // Returns null for a dive of its own, for the first dive of a day, and when
  // the previous dive has not been computed yet - the last case being logged,
  // because the plan then shown is the one of an isolated dive.
  dayPreviousDive() {
    const day = window.store.state.day
    const index = day?.editingIndex
    if (index === null || index === undefined || index <= 0) return null

    const previous = day.dives[index - 1]
    const dive = day.dives[index]
    if (!previous || !dive) {
      console.error('PlanView.dayPreviousDive: dive #%o of the day is edited but the series does not carry it, this dive is planned on its own', index)
      return null
    }
    if (!previous.results) {
      console.error('PlanView.dayPreviousDive: dive #%d of the day is edited while dive #%d carries no computed plan, this dive is planned as a first dive of the day',
        index + 1, index)
      return null
    }

    return previousDiveRecord(previous.results, dive.surfaceIntervalBefore, 'PlanView.dayPreviousDive')
  }

  computePlan() {
    const { segments, settings, ccr, gases, diluents } = window.store.state
    const shared = { settings, environment: this.environment(), gases, diluents }
    const previousDive = this.dayPreviousDive()

    const enabledGases = gases.filter(g => g.enabled)
    // Same preparation as planDive, so the school mode compares gradient
    // factors on the very segments - descent deducted, gas per segment
    // resolved - the main plan was computed on. Computed after planDive, which
    // is the one to refuse a segment and to name why.
    this.results = planDive({ shared, dive: { segments, ccr }, previousDive })
    const adjusted = this.adjustSegmentsForDescent(segments, settings)
    const adjustedSegments = ccr.enabled ? adjusted : resolveSegmentGases(adjusted, enabledGases, 'PlanView.computePlan', gases)

    let ccrContext = null

    if (ccr.enabled) {
      ccrContext = this.buildCCRConfig(adjustedSegments, segments, diluents, ccr)

      this.renderCCRResults(this.results.ccr)
      this.renderBailoutResults(this.results.bailout, true)
      this.renderGasConsumption(this.results.bailout.gasConsumption)

      document.getElementById('ccr-results').classList.remove('hidden')
      document.getElementById('gas-consumption').classList.remove('hidden')
    } else {
      this.renderBailoutResults(this.results.bailout, false)
      this.renderGasConsumption(this.results.bailout.gasConsumption, false)

      document.getElementById('ccr-results').classList.add('hidden')
      document.getElementById('gas-consumption').classList.remove('hidden')
    }

    document.getElementById('results-container').classList.remove('hidden')

    const schoolModeResults = this.computeSchoolModeResults(
      settings, ccr, enabledGases, adjustedSegments, ccrContext, previousDive
    )
    window.store.setResults(this.results.ccr, this.results.bailout, schoolModeResults)
  }

  renderCCRResults(plan) {
    const tableEl = document.getElementById('ccr-table')
    const summaryEl = document.getElementById('ccr-summary')

    tableEl.innerHTML = this.renderPlanTable(plan, true)
    summaryEl.innerHTML = this.renderSummary(plan)
  }

  renderBailoutResults(plan, isBailout) {
    const tableEl = document.getElementById('bailout-table')
    const summaryEl = document.getElementById('bailout-summary')
    const titleEl = document.getElementById('bailout-title')

    titleEl.textContent = isBailout ? this.t('plan_bailout') : this.t('plan_deco')
    // For bailout: show all segments including ascents and gas switches
    tableEl.innerHTML = this.renderPlanTable(plan, false)
    summaryEl.innerHTML = this.renderSummary(plan)
  }

  // Label the Gas column prints for a gas, shortened only where the short form
  // still names one cylinder and one only.
  //
  // Resolved over every name the screen can show - both tables and the
  // requirement table below them - and not per table: shortGasName called on a
  // single row knows nothing of the rest of the page, so a 'TX 17/37' could
  // print short in the loop table and whole in the bailout one, for the same
  // cylinder. The cache is dropped whenever the results change (render,
  // clearResultsDisplay).
  gasLabelOf(name) {
    if (!name) return ''
    if (!this.gasLabelCache) {
      const names = new Set()
      for (const g of (window.store.getBailoutGases() || [])) if (g?.name) names.add(g.name)
      for (const plan of [this.results?.ccr, this.results?.bailout]) {
        if (plan?.ccrPhase?.gas) names.add(plan.ccrPhase.gas)
        for (const seg of (plan?.segments || [])) {
          if (seg.gas) names.add(seg.gas)
          if (seg.gasName) names.add(seg.gasName)
        }
        for (const c of (plan?.gasConsumption || [])) if (c.name) names.add(c.name)
      }
      const ordered = [...names]
      const labels = gasLabels(ordered)
      this.gasLabelCache = new Map(ordered.map((n, i) => [n, labels[i]]))
    }
    return this.gasLabelCache.get(name) ?? shortGasName(name)
  }

  // Render an HTML table of dive plan segments with columns for index, type,
  // depth, time, runtime, gas, ppO2, END, and tissue info. Runs analyzeAlerts
  // for per-segment alert badges and an alerts summary section at the bottom.
  renderPlanTable(plan, isCCR) {
    if (!plan.segments || plan.segments.length === 0) {
      return `<p class="text-success">${this.t('plan_no_deco')}</p>`
    }

    const displaySegments = filterDisplaySegments(plan.segments)

    // Offset for total time (CCR time before bailout)
    const runtimeOffset = plan.runtimeOffset || 0
    const isBailout = runtimeOffset > 0

    // Analyze alerts for all segments.
    // plan.warnings carries what the engine had to give up on (a stop cut at
    // MAX_STOP_TIME, the runtime limit, the fallback to air): those are turned
    // into error alerts by analyzeAlerts so the diver sees that the displayed
    // plan is not a complete decompression.
    const settings = window.store.state.settings || {}
    // Density column, shown on every row and not only where an alert fires:
    // reading it climb from 3 to 8 g/l along a profile is what teaches the
    // diver where the work of breathing comes from.
    const showDensity = settings.showGasDensity !== false
    const { segmentAlerts, alerts } = analyzeAlerts(displaySegments, {
      ccrPhase: plan.ccrPhase,
      isCCR,
      environment: this.environment(),
      translateFn: (key, params) => this.translateAlert(key, params),
      showNarcosis: settings.showNarcosisAlerts !== false,
      showDensity: settings.showDensityAlerts !== false,
      showIcd: settings.showIcdAlerts !== false,
      trackPerSegment: true,
      planWarnings: plan.warnings || []
    })

    // CCR phase row for bailout table (shows runtime as total since it's before bailout)
    const ccrPhaseRow = plan.ccrPhase ? `
      <tr class="bg-base-200 opacity-70">
        <td>-</td>
        <td>CCR</td>
        <td>${plan.ccrPhase.depth}m</td>
        <td>-</td>
        <td>${Math.ceil(plan.ccrPhase.runtime)} min</td>
        <td>${this.gasLabelOf(plan.ccrPhase.gas)} SP${plan.ccrPhase.setpoint}</td>
        <td class="${plan.ccrPhase.ppO2 > 1.4 ? 'text-warning' : ''}">${plan.ccrPhase.ppO2?.toFixed(2) || '-'}</td>
        <td>${plan.ccrPhase.end !== undefined && plan.ccrPhase.end >= 0 ? plan.ccrPhase.end + 'm' : '-'}</td>
        ${showDensity ? `<td class="${this.densityClass(plan.ccrPhase, isCCR)}">${formatDensity(plan.ccrPhase, this.environment())}</td>` : ''}
        <td>-</td>
      </tr>
    ` : ''

    return `
      <table class="table table-xs">
        <thead>
          <tr>
            <th>#</th>
            <th>${this.t('plan_type')}</th>
            <th>${this.t('plan_depth')}</th>
            <th>${this.t('plan_time')}</th>
            <th>${this.t('plan_runtime')}</th>
            <th>${this.t('plan_gas')}</th>
            ${isCCR ? `<th>${this.t('plan_setpoint')}</th>` : ''}
            <th>${this.t('plan_ppo2')}</th>
            <th>END</th>
            ${showDensity ? `<th>${this.t('plan_density')} (g/l)</th>` : ''}
            <th>${this.t('plan_tissue')}</th>
          </tr>
        </thead>
        <tbody>
          ${ccrPhaseRow}
          ${displaySegments.map((s, i) => {
      const depthDisplay = s.type === 'ascent' || s.type === 'descent'
        ? `${s.startDepth} -> ${s.endDepth}m`
        : `${s.depth}m`
      const typeLabel = this.getSegmentTypeLabel(s)
      // Alert badges for this segment
      const segAlerts = segmentAlerts.get(i)
      // A segment carrying an error alert (ceiling violation, hypoxia,
      // hyperoxia...) is highlighted as a whole: the badge alone, in a single
      // cell of a dense table, is far too easy to miss.
      const hasErrorAlert = Array.isArray(segAlerts) && segAlerts.some(a => a.type === 'error')
      const rowClass = [
        s.type === 'stop' ? 'text-warning' : (s.isBailDelay ? 'text-error font-bold' : ''),
        hasErrorAlert ? 'bg-error/20 font-bold' : ''
      ].filter(Boolean).join(' ')
      // For bailout, show total runtime (CCR + bailout); otherwise show segment runtime
      const displayRuntime = isBailout ? Math.ceil(s.runtime + runtimeOffset) : Math.ceil(s.runtime)
      // Tissue info display
      const tissueDisplay = s.tissueInfo
        ? `C${s.tissueInfo.compartment} ${s.tissueInfo.gfPercent}% ${s.tissueInfo.leadingGas}`
        : '-'

      return `
              <tr class="${rowClass}">
                <td>${i + 1}</td>
                <td>${typeLabel}</td>
                <td>${depthDisplay}${this.renderUncoveredAlertBadge(segAlerts)}</td>
                <td>${Math.ceil(s.time)} min</td>
                <td>${displayRuntime} min</td>
                <td>${escapeHtml(this.gasLabelOf(s.gas))}${s.gasPressureLeft !== undefined ? ' (' + s.gasPressureLeft + 'bars)' : ''} ${this.renderAlertBadge(segAlerts, 'gas')}</td>
                ${isCCR ? `<td>${s.setpoint || '-'}</td>` : ''}
                <td class="${s.ppO2 > 1.4 ? 'text-warning' : ''}">${s.ppO2?.toFixed(2) || '-'}${this.renderAlertBadge(segAlerts, 'ppO2')}</td>
                <td>${s.end !== undefined && s.end >= 0 ? s.end + 'm' : '-'}${this.renderAlertBadge(segAlerts, 'end')}</td>
                ${showDensity ? `<td class="${this.densityClass(s, isCCR)}">${formatDensity(s, this.environment())}${this.renderAlertBadge(segAlerts, 'density')}</td>` : ''}
                <td class="text-xs">${tissueDisplay}</td>
              </tr>
            `
    }).join('')}
        </tbody>
      </table>

      ${plan.stops.length > 0 ? `
        <div class="mt-3">
          <h4 class="text-xs font-bold mb-1">${this.t('plan_stops_summary')}</h4>
          <div class="flex flex-wrap gap-2">
            ${plan.stops.map(s => `
              <div class="badge badge-outline">${s.depth}m: ${s.time}min</div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${this.renderAlertsSummary(alerts)}
    `
  }

  // Colour of a density cell. Same thresholds as the density alerts, read from
  // the same place, so a row can never be painted green under an alert that
  // fired on it.
  densityClass(segment, isCCR) {
    const level = densityLevel(segmentDensity(segment, this.environment()), isCCR)
    if (level === 'critical') return 'text-error font-bold'
    if (level === 'high') return 'text-warning'
    return ''
  }

  getSegmentTypeLabel(segment) {
    if (segment.isBailDelay) return this.t('segment_bail')
    switch (segment.type) {
      case 'descent': return '↘'
      case 'ascent': return '↗'
      case 'bottom': return '→'
      case 'stop': return segment.safety ? '🛟' : '💤'
      case 'bail': return '💥'
      case 'gasChange': return "🤟"
      case 'diluentChange': return "🔄"
      default: return segment.type
    }
  }

  // Fields that already have their own badge in a dedicated column
  static BADGED_ALERT_FIELDS = ['gas', 'ppO2', 'end']

  // Render alert badge for a cell (clickable, scrolls to alerts)
  renderAlertBadge(segmentAlerts, field) {
    if (!segmentAlerts) return ''
    const alert = segmentAlerts.find(a => a.field === field)
    if (!alert) return ''
    return this.alertBadgeHtml(alert.type)
  }

  // Render the badge of the alerts that target no existing column: the ceiling
  // violation (field 'depth'), the gas density and the bailout ppN2 increase.
  // It is shown next to the depth, the reference the ceiling is compared to.
  // Without it those alerts would only exist in the summary at the bottom of
  // the table, with no way to know which line is at fault.
  renderUncoveredAlertBadge(segmentAlerts) {
    if (!segmentAlerts) return ''
    const uncovered = segmentAlerts.filter(a => !PlanView.BADGED_ALERT_FIELDS.includes(a.field))
    if (uncovered.length === 0) return ''
    return this.alertBadgeHtml(uncovered.some(a => a.type === 'error') ? 'error' : 'warning')
  }

  // Clickable badge scrolling to the alerts summary
  alertBadgeHtml(type) {
    const badgeClass = type === 'error' ? 'badge-error' : 'badge-warning'
    return `<span class="badge badge-xs ${badgeClass} ml-1 cursor-pointer" onclick="document.getElementById('alerts-summary')?.scrollIntoView({behavior:'smooth'})">!</span>`
  }

  // Render alerts summary section
  renderAlertsSummary(alerts) {
    if (!alerts || alerts.length === 0) return ''

    const errorAlerts = alerts.filter(a => a.type === 'error')
    const warningAlerts = alerts.filter(a => a.type === 'warning')

    return `
      <div id="alerts-summary" class="mt-3 space-y-2">
        <h3 class="font-semibold text-sm">${this.t('alerts_summary_title')}</h3>
        ${errorAlerts.length > 0 ? `
          <div class="alert alert-error py-2 font-bold">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div class="flex flex-col gap-1">
              ${errorAlerts.map(a => `<span class="text-xs">${a.message}</span>`).join('')}
            </div>
          </div>
        ` : ''}
        ${warningAlerts.length > 0 ? `
          <div class="alert alert-warning py-2">
            <div class="flex flex-col gap-1">
              ${warningAlerts.map(a => `<span class="text-xs">${a.message}</span>`).join('')}
            </div>
          </div>
        ` : ''}
      </div>
    `
  }

  renderSummary(plan) {
    // For bailout, total runtime includes CCR time before bailout, "1" is added to take the last stop to surface ascent time
    const totalRuntime = Math.ceil(plan.runtime + (plan.runtimeOffset || 0))
    const runtimeLabel = plan.runtimeOffset ? this.t('plan_total_runtime') : this.t('plan_runtime')

    // Surface interval, below the stats: the diver leaves the water still
    // loaded, and the plan is the only place that knows by how much. The
    // no-fly cell holds a DAN recommendation, so the rule behind it is spelled
    // out right under the stats rather than left to be guessed.
    const t = this.t.bind(this)
    const { desat, danCase, danRule, danSource } = surfaceIntervalSentences(plan, t)
    const noFly = noFlyValue(plan, t)
    // Oxygen exposure through the single helper: on a repetitive dive the
    // figures are those of the series, not of this dive alone
    const exposure = exposureShown(plan)
    const sentences = [desat, danCase, danRule, danSource].filter(Boolean).join(' ')
    const surfaceInterval = sentences
      ? `<p class="text-xs opacity-70 mt-2 px-1">${sentences}</p>`
      : ''
    const noFlyStat = noFly
      ? `
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t('plan_no_fly')}</div>
          <div class="stat-value text-base">${noFly}</div>
        </div>
      `
      : ''

    return `
      <div class="stats stats-horizontal shadow w-full text-center">
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t('plan_tts')}</div>
          <div class="stat-value text-base">${plan.tts + 1} min</div>
        </div>
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${runtimeLabel}</div>
          <div class="stat-value text-base">${totalRuntime + 1} min</div>
        </div>
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t(exposure.series ? 'plan_cns_series' : 'plan_cns')}</div>
          <div class="stat-value text-base ${exposure.cnsWarning ? 'text-warning' : ''}">${exposure.cns}%</div>
        </div>
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t(exposure.series ? 'plan_otu_series' : 'plan_otu')}</div>
          <div class="stat-value text-base ${exposure.otuWarning ? 'text-warning' : ''}">${exposure.otu}</div>
        </div>
        ${noFlyStat}
      </div>
      ${surfaceInterval}
    `
  }

  renderGasConsumption(consumption, isBailout = true) {
    const tableEl = document.getElementById('gas-table')
    const titleEl = document.getElementById('gas-consumption-title')
    const settings = window.store.state.settings

    // Update title based on mode
    if (titleEl) {
      titleEl.textContent = isBailout ? this.t('plan_gas_requirements') : this.t('plan_gas_consumption')
    }

    if (!consumption || consumption.length === 0) {
      tableEl.innerHTML = `<p class="text-sm opacity-70">-</p>`
      return
    }

    const hasInsufficient = consumption.some(c => c.liters > 0 && c.insufficient)

    // RMV info: for bailout show stress/deco, for OC show bottom/deco
    const rmvInfo = isBailout
      ? `${this.t('plan_based_on_rmv')} ${settings.rmvStress}/${settings.rmvDeco} L/min`
      : `${this.t('plan_based_on_rmv')} ${settings.rmvBottom}/${settings.rmvDeco} L/min`

    tableEl.innerHTML = `
      <table class="table table-xs">
        <thead>
          <tr>
            <th>${this.t('plan_gas')}</th>
            <th>${this.t('plan_tank')}</th>
            <th>${this.t('plan_needed')}</th>
            <th>${this.t('plan_status')}</th>
          </tr>
        </thead>
        <tbody>
          ${gasConsumptionRows(consumption, (key, params) => this.t(key, params)).map(r => {
      const rowClass = r.insufficient ? 'text-error font-bold' : (r.unused ? 'opacity-60' : '')
      // A cylinder emptied to the litre gets its own colour: printed green
      // beside one with a thousand litres to spare, it read as comfortable.
      const statusClass = r.insufficient
        ? 'text-error'
        : (r.unused ? 'opacity-70' : (r.tight ? 'text-warning font-bold' : 'text-success'))
      const statusIcon = `<span class="${statusClass}">${escapeHtml(r.statusText)}</span>`

      return `
              <tr class="${rowClass}">
                <td>${escapeHtml(r.label)}</td>
                <td>${escapeHtml(r.tankText)}</td>
                <td>${escapeHtml(r.neededText)}</td>
                <td>
                  ${statusIcon}
                  <div class="text-xs opacity-70">${escapeHtml(r.detailText)}</div>
                  ${r.backupText ? `<div class="text-xs opacity-70">${escapeHtml(r.backupText)}</div>` : ''}
                </td>
              </tr>
            `
    }).join('')}
        </tbody>
      </table>
      ${hasInsufficient ? `
        <div class="alert alert-error mt-3 py-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span class="text-sm">${this.t('plan_insufficient_gas')}</span>
        </div>
      ` : ''}
      <p class="text-xs opacity-70 mt-2">
        ${rmvInfo}
      </p>
    `
  }
}
